import type { MouseEvent, ReactElement } from 'react';
import type { HouseholdSearchResponse } from 'src/utils/household/useHouseholdSearch';
import { cloneElement, isValidElement, useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/navigation';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@savvy-kit/components/command';
import { Spinner } from '@savvy-kit/components/spinner';
import { cn } from '@savvy-kit/lib/utils';
import ToggleTabs from '@savvy-kit/patterns/toggle-tabs';
import { FileX, SearchIcon, X } from 'lucide-react';
import useDebounce from 'src/hooks/use-debounce';
import { useRecentSearches } from 'src/hooks/use-recent-searches';
import { isClient } from 'src/utils/advisor-dash/household';
import { useHouseholdSearch } from 'src/utils/household/useHouseholdSearch';
import { convertSnakeCaseToSentenceCase } from 'src/utils/url';

import { useTrackEvent } from '@savvy/deprecated-shared/tracking/use-track-event';

import { HouseholdSearchResult } from '../household-search-result-with-attribution';

// Cycling input placeholder — only examples for fields the backend actually
// searches (household/client name, email, phone).
const PLACEHOLDER_EXAMPLES = [
  'Search by household, name, email, or phone',
  'Try "Patterson"',
  'Search by email…',
  'Try "@example.com"',
  'Search by phone…',
  'Try "(415) 555"',
];

function useShuffled<T>(items: readonly T[]): readonly T[] {
  // Cosmetic ordering only — a random-sort is good enough for placeholder cycling.
  return useMemo(() => [...items].sort(() => Math.random() - 0.5), [items]);
}

function AnimatedPlaceholder({ active }: { readonly active: boolean }) {
  const examples = useShuffled(PLACEHOLDER_EXAMPLES);
  const [index, setIndex] = useState(0);
  const [phase, setPhase] = useState<'in' | 'visible' | 'out'>('in');

  useEffect(() => {
    if (!active) return undefined;
    const durations = { in: 200, visible: 1800, out: 200 };
    const timer = setTimeout(() => {
      if (phase === 'in') setPhase('visible');
      else if (phase === 'visible') setPhase('out');
      else {
        setIndex((i) => (i + 1) % examples.length);
        setPhase('in');
      }
    }, durations[phase]);
    return () => clearTimeout(timer);
  }, [phase, active, examples.length]);

  useEffect(() => {
    if (active) {
      setIndex(0);
      setPhase('in');
    }
  }, [active]);

  if (!active) return null;

  return (
    <span
      aria-hidden
      className={cn(
        'pointer-events-none absolute inset-y-0 left-9 flex select-none items-center text-sm text-secondary transition-all duration-200',
        phase === 'in' && 'translate-y-1.5 opacity-0',
        phase === 'visible' && 'translate-y-0 opacity-100',
        phase === 'out' && '-translate-y-1.5 opacity-0',
      )}
    >
      {examples[index]}
    </span>
  );
}

type TriggerChild = ReactElement<{ onClick?: (e: MouseEvent<HTMLElement>) => void }>;

type GlobalSearchDialogProps = {
  readonly children: TriggerChild;
  readonly open?: boolean;
  readonly onOpenChange?: (open: boolean) => void;
  readonly userId?: string | null;
};

type ResultEdge = HouseholdSearchResponse['households']['edges'][number];
type ResultHousehold = ResultEdge['node'];

function relativeTime(viewedAt: number): string {
  const minutes = Math.floor((Date.now() - viewedAt) / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'Yesterday';
  return `${days} days ago`;
}

function GlobalSearchDialog({ children, open, onOpenChange, userId = null }: GlobalSearchDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = open ?? internalOpen;
  const setIsOpen = (next: boolean) => {
    if (open === undefined) setInternalOpen(next);
    onOpenChange?.(next);
  };
  const [searchBarText, setSearchBarText] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | 'client' | 'prospect'>('all');
  const router = useRouter();

  const debouncedSearchBarText = useDebounce(searchBarText, 250);
  const trimmedQuery = debouncedSearchBarText.trim();
  const hasQuery = trimmedQuery.length > 0;

  // navigator is unavailable during SSR for client components, so detect after
  // mount. platform can be empty in some environments — fall back to userAgent.
  const [isMac, setIsMac] = useState(true);
  useEffect(() => {
    setIsMac(/mac/i.test(navigator.platform || navigator.userAgent));
  }, []);

  const { data, loading } = useHouseholdSearch({ query: trimmedQuery });
  const edges = data?.households.edges ?? [];
  const clientEdges = edges.filter((e) => isClient(e.node.householdStage));
  const prospectEdges = edges.filter((e) => !isClient(e.node.householdStage));
  const filteredEdges = { all: edges, client: clientEdges, prospect: prospectEdges }[typeFilter];
  const totalCount = data?.households.totalCount ?? edges.length;

  useEffect(() => {
    setTypeFilter('all');
  }, [trimmedQuery]);

  useEffect(() => {
    if (!isOpen) {
      setSearchBarText('');
      setTypeFilter('all');
    }
  }, [isOpen]);

  const { recents, addRecent } = useRecentSearches(userId);
  const trackInteraction = useTrackEvent('userInteraction');

  function handleSelect(household: ResultHousehold) {
    const isClientHousehold = isClient(household.householdStage);
    const primaryEmployer = household.primaryClient?.employerName ?? '';
    const coEmployer = household.coClient?.employerName ?? '';
    const stage = convertSnakeCaseToSentenceCase(household.householdStage ?? '');
    const subtitle = isClientHousehold
      ? [primaryEmployer, coEmployer].filter(Boolean).join(', ')
      : [primaryEmployer, stage].filter(Boolean).join(' — ');
    addRecent({
      id: household.id,
      name: household.name,
      salesforceAccountId: household.salesforceAccountId ?? null,
      type: isClientHousehold ? 'client' : 'prospect',
      subtitle: subtitle || undefined,
    });
    trackInteraction({
      elementName: 'global-household-search-result',
      htmlElement: 'div',
      interactionType: 'click',
    });
    if (household.salesforceAccountId) {
      router.push(`/advisor/dashboard/client/${household.salesforceAccountId}`);
      setIsOpen(false);
    }
  }

  const trigger = isValidElement(children)
    ? cloneElement(children, {
        onClick: (e: MouseEvent<HTMLElement>) => {
          children.props.onClick?.(e);
          setIsOpen(true);
        },
      })
    : children;

  function renderEmpty() {
    if (recents.length > 0) {
      return (
        <CommandGroup
          heading="Recently viewed"
          className="!px-0 [&_[cmdk-group-heading]]:!px-4 [&_[cmdk-group-heading]]:py-2 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-secondary"
        >
          {recents.map((recent) => (
            <CommandItem
              key={recent.id}
              value={recent.id}
              onSelect={() => {
                if (recent.salesforceAccountId) {
                  router.push(`/advisor/dashboard/client/${recent.salesforceAccountId}`);
                }
                setIsOpen(false);
              }}
              className="flex cursor-pointer items-center gap-3 rounded-none !px-4 !py-2.5 data-[selected=true]:bg-component-hover"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <p className="truncate text-xs font-medium text-dark">{recent.name}</p>
                  <span className="shrink-0 text-xs text-secondary">
                    · {recent.type === 'client' ? 'Client' : 'Prospect'}
                  </span>
                </div>
                {recent.subtitle && <p className="truncate text-[12px] text-secondary">{recent.subtitle}</p>}
              </div>
              <span className="shrink-0 text-xs text-secondary">{relativeTime(recent.viewedAt)}</span>
            </CommandItem>
          ))}
        </CommandGroup>
      );
    }
    return (
      <div className="flex min-h-[420px] flex-col items-center justify-center px-6 text-center">
        <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-slate-100">
          <SearchIcon className="h-6 w-6 text-secondary" aria-hidden="true" />
        </div>
        <p className="text-[14px] font-medium text-dark">Search for a household</p>
        <p className="mt-1 max-w-[320px] text-xs text-secondary">
          Find any household by name, first or last name, email, or phone.
        </p>
      </div>
    );
  }

  function renderResults() {
    if (filteredEdges.length === 0) {
      // Empty because the tab filter excludes an otherwise-hit result set vs.
      // the query genuinely matching nothing — distinct messages so we don't
      // tell the advisor their (working) search failed.
      const tabEmpty = edges.length > 0;
      const tabLabel = typeFilter === 'client' ? 'clients' : 'prospects';
      return (
        <CommandEmpty className="py-16">
          <div className="flex flex-col items-center">
            <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-slate-100">
              <FileX className="h-6 w-6 text-primary" aria-hidden="true" />
            </div>
            <p className="text-[15px] font-semibold text-dark">
              {tabEmpty ? `No ${tabLabel} match "${trimmedQuery}"` : `No households match "${trimmedQuery}"`}
            </p>
            <p className="mt-1.5 max-w-[340px] text-center text-xs text-secondary">
              {tabEmpty
                ? `Switch to the All tab to see all ${totalCount} results.`
                : 'Try a household name, first or last name, email, or phone number.'}
            </p>
          </div>
        </CommandEmpty>
      );
    }
    return (
      <>
        {filteredEdges.map((edge) => {
          const household = edge.node;
          return (
            <CommandItem
              key={household.id}
              value={household.id}
              onSelect={() => handleSelect(household)}
              className="cursor-pointer items-start gap-3 rounded-none border-b border-slate-100 !px-4 py-3 last:border-0 data-[selected=true]:bg-component-hover"
            >
              <HouseholdSearchResult
                household={household}
                matchInfos={edge.matchInfos}
                query={trimmedQuery}
                tags={household.organizationHouseholdTags.map((t) => ({
                  id: t.id,
                  name: t.name,
                  hexColorCode: null,
                  emoji: null,
                  isSmart: false,
                }))}
              />
            </CommandItem>
          );
        })}
      </>
    );
  }

  function renderBody() {
    if (hasQuery && loading && edges.length === 0) {
      return (
        <div className="flex justify-center items-center py-8">
          <Spinner className="size-6 text-primary" />
        </div>
      );
    }
    if (!hasQuery) return renderEmpty();
    return renderResults();
  }

  return (
    <>
      {trigger}
      <CommandDialog
        open={isOpen}
        onOpenChange={setIsOpen}
        title="Search Household"
        description="Search by household, name, email, or phone"
        shouldFilter={false}
        showCloseButton={false}
        className="max-w-[720px]"
      >
        <div className="relative [&_[data-slot=command-input-wrapper]]:border-slate-200">
          <CommandInput placeholder="" value={searchBarText} onValueChange={setSearchBarText} aria-label="Search" />
          <AnimatedPlaceholder active={searchBarText.length === 0} />
          <div className="absolute right-3 top-1/2 flex -translate-y-1/2 items-center gap-2">
            {searchBarText.length > 0 && (
              <button
                type="button"
                aria-label="Clear search"
                onClick={() => setSearchBarText('')}
                className="flex size-5 shrink-0 items-center justify-center text-secondary hover:text-dark"
              >
                <X className="size-4" />
              </button>
            )}
            <kbd className="pointer-events-none select-none rounded-md border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-xs font-medium text-secondary">
              {isMac ? '⌘K' : 'Ctrl+K'}
            </kbd>
          </div>
        </div>
        {/* Always render the tab row so the modal height stays constant when
            results arrive; until there are results it's hidden but keeps its
            space (the only element that previously changed the height). */}
        <div
          className={cn(
            'border-b px-4 py-1.5 text-[14px]',
            hasQuery && edges.length > 0 ? 'border-slate-200' : 'border-transparent [&_*]:invisible',
          )}
        >
          <ToggleTabs
            activeTab={typeFilter}
            setActiveTab={(t) => {
              if (t === 'client' || t === 'prospect') setTypeFilter(t);
              else setTypeFilter('all');
            }}
            tabs={[
              { name: `All (${totalCount})`, value: 'all' },
              { name: `Clients (${clientEdges.length})`, value: 'client' },
              { name: `Prospects (${prospectEdges.length})`, value: 'prospect' },
            ]}
          />
        </div>
        <CommandList className="min-h-[420px] max-h-[420px] overflow-y-auto">{renderBody()}</CommandList>
        <div className="flex items-center gap-4 border-t border-slate-200 px-4 py-2.5">
          {[
            { key: '↑↓', label: 'Navigate' },
            { key: '↵', label: 'Open' },
            { key: 'esc', label: 'Close' },
          ].map((hint) => (
            <span key={hint.label} className="flex items-center gap-1.5 text-xs text-secondary">
              <kbd className="rounded border border-slate-200 bg-slate-50 px-1 py-0.5 text-xs font-medium">
                {hint.key}
              </kbd>
              {hint.label}
            </span>
          ))}
        </div>
      </CommandDialog>
    </>
  );
}

export default GlobalSearchDialog;
